import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getServiceErrorKey } from '../serviceErrors'
import { useConfirmationResult } from '../useConfirmationResult'

import { holderLabel, useServiceRoleLabels } from './roles'
import { ConfirmationResultModal, FormField, Select } from '@percent/lemonade'
import type { SelectOption } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  isInternalServiceRole,
  type InternalServiceRole,
  type InternalServiceRoleHolder
} from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'

type ChangeServiceRoleModalProps = {
  holder: InternalServiceRoleHolder
  onClose: () => void
  onChanged: () => void
}

export function ChangeServiceRoleModal({ holder, onClose, onChanged }: Readonly<ChangeServiceRoleModalProps>) {
  const { t } = useTranslation()
  const { internalServiceService } = useServices()
  const { options, labelOf } = useServiceRoleLabels()
  const [role, setRole] = useState<InternalServiceRole>(holder.role)
  const [{ isPending }, { mutateAsync: changeRole }] = useMutation(internalServiceService.changeServiceRole)
  const result = useConfirmationResult({ onClose, onSuccess: onChanged, isBusy: isPending })
  const name = holderLabel(holder)

  const confirm = async () => {
    const changed = await result.run(() => changeRole({ serviceId: holder.serviceId, holderId: holder.holderId, role }))

    // A refusal means the roles shown were out of date, so they are read again
    if (!changed) onChanged()
  }

  return (
    <ConfirmationResultModal
      isOpen
      state={result.state}
      title={t('modal.changeServiceRole.title', { name })}
      primaryButtonText={t('button.saveChanges')}
      loadingText={t('modal.changeServiceRole.button.saving')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={confirm}
      onClose={result.dismiss}
      onFailureClose={result.backToConfirm}
      disabled={role === holder.role || isPending}
      loading={isPending}
      primaryBtnTestId="change-service-role-submit-button"
      secondaryBtnTestId="change-service-role-cancel-button"
      buttonTestId="change-service-role-result-button"
      success={{
        title: t('modal.changeServiceRole.success.title'),
        description: t('modal.changeServiceRole.success.description', { name, role: labelOf(role) }),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.changeServiceRole.error.title'),
        description: t(getServiceErrorKey(result.error) ?? 'modal.changeServiceRole.error.description'),
        button: t('button.close')
      }}
    >
      <FormField label={t('typography.role')} necessity="required">
        <Select
          name="role"
          defaultValue={options.find(option => option.value === role)}
          options={options}
          onChange={(option: SelectOption) => isInternalServiceRole(option.value) && setRole(option.value)}
          data-testid="change-service-role-select"
        />
      </FormField>
    </ConfirmationResultModal>
  )
}
